/**
 * /pages/store-analysis-custom controller.
 * Sibling of assets/store-analysis.js — see the spec at
 * docs/superpowers/specs/2026-05-16-store-analysis-custom-design.md
 *
 * Modes:
 *  - "single" (default): one parent URL + N offer URLs
 *  - "bulk": a JSON array of { name?, parent, offers: [] } objects. After Add,
 *    an extra "Active bulk offer" dropdown appears; switching it swaps the
 *    currentContext that card clicks read from.
 */
(function () {
  'use strict';

  const OFFERS_CATALOG = [
    { offerId: "FBT-958462", offerType: "PB",  industry: "HOME DECOR",         subIndustry: "HOME & LIFESTYLE"        },
    { offerId: "FBT-107578", offerType: "PB",  industry: "FASHION & APPAREL",  subIndustry: "JEWELRY"                 },
    { offerId: "FBT-672486", offerType: "PB",  industry: "BEAUTY & SKINCARE",  subIndustry: "BEAUTY & COSMETICS"      },
    { offerId: "FBT-246623", offerType: "PB",  industry: "BEAUTY & SKINCARE",  subIndustry: "BEAUTY & COSMETICS"      },
    { offerId: "FBT-367985", offerType: "PB",  industry: "HOME & LIFESTYLE",   subIndustry: "HOME DECOR"              },
    { offerId: "FBT-534220", offerType: "FBT", industry: "ELECTRONICS & TECH", subIndustry: "PHONE ACCESSORIES"       },
    { offerId: "FBT-281278", offerType: "FBT", industry: "BEAUTY & SKINCARE",  subIndustry: "BEAUTY & COSMETICS"      },
    { offerId: "FBT-025682", offerType: "FBT", industry: "FOOTWEAR",           subIndustry: "ACTIVEWEAR & ATHLEISURE" }
  ];

  const STORAGE_KEY = 'storeAnalysisSkailamaPreview';

  // ─── DOM refs ───────────────────────────────────────────────────────────
  const root = document.querySelector('[data-store-analysis-custom]');
  if (!root) return;

  const form              = root.querySelector('[data-sac-form]');
  const modeBtns          = Array.from(root.querySelectorAll('[data-sac-mode-btn]'));
  const singlePanel       = root.querySelector('[data-sac-panel="single"]');
  const bulkPanel         = root.querySelector('[data-sac-panel="bulk"]');
  const parentUrlInp      = root.querySelector('[data-sac-parent-url]');
  const offerListEl       = root.querySelector('[data-sac-offer-list]');
  const offerAddBtn       = root.querySelector('[data-sac-offer-add]');
  const bulkTextarea      = root.querySelector('[data-sac-bulk-json]');
  const submitBtn         = form ? form.querySelector('.sac-form__submit') : null;
  const errorEl           = root.querySelector('[data-sac-error]');
  const summaryEl         = root.querySelector('[data-sac-summary]');
  const summaryHostEl     = root.querySelector('[data-sac-summary-host]');
  const summaryParentEl   = root.querySelector('[data-sac-summary-parent]');
  const summaryOffersEl   = root.querySelector('[data-sac-summary-offers]');
  const bulkPickerWrap    = root.querySelector('[data-sac-bulk-picker]');
  const bulkSelect        = root.querySelector('[data-sac-bulk-select]');
  const cardsWrap         = root.querySelector('[data-sac-cards-wrap]');
  const cardsHint         = root.querySelector('[data-sac-cards-hint]');
  const cardsHost         = root.querySelector('[data-sac-cards]');
  const cardsEmptyEl      = root.querySelector('[data-sac-cards-empty]');
  const filterSelects     = Array.from(root.querySelectorAll('[data-sac-filter]'));

  let mode = 'single';                   // 'single' | 'bulk'
  let currentContext = null;             // active context (single or selected bulk entry)
  let bulkContexts = [];                 // array of contexts when mode === 'bulk'

  // ─── Helpers: UI status ─────────────────────────────────────────────────
  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function showError(msg) {
    if (!errorEl) return;
    if (!msg) { errorEl.textContent = ''; errorEl.setAttribute('hidden', ''); return; }
    errorEl.textContent = msg;
    errorEl.removeAttribute('hidden');
  }

  function clearError() { showError(''); }

  // ─── Mode toggle ────────────────────────────────────────────────────────
  function setMode(next) {
    mode = (next === 'bulk') ? 'bulk' : 'single';
    modeBtns.forEach(function (b) {
      const active = b.getAttribute('data-sac-mode-btn') === mode;
      b.classList.toggle('is-active', active);
      b.setAttribute('aria-selected', active ? 'true' : 'false');
    });
    if (singlePanel) singlePanel.hidden = (mode !== 'single');
    if (bulkPanel)   bulkPanel.hidden   = (mode !== 'bulk');
    // Reset context whenever the mode switches — user must re-Add.
    currentContext = null;
    bulkContexts = [];
    setCardsEnabled(false);
    if (summaryEl) summaryEl.setAttribute('hidden', '');
    if (bulkPickerWrap) bulkPickerWrap.setAttribute('hidden', '');
    clearError();
  }

  modeBtns.forEach(function (b) {
    b.addEventListener('click', function () { setMode(b.getAttribute('data-sac-mode-btn')); });
  });

  // ─── URL parsing ────────────────────────────────────────────────────────
  function parseProductUrl(raw) {
    const v = String(raw || '').trim();
    if (!v) throw new Error('Empty URL.');
    let u;
    try { u = new URL(v); }
    catch (e) { throw new Error('Invalid URL: ' + v); }
    let m = u.pathname.match(/^\/products\/([^/?#]+)\/?$/);
    if (!m) m = u.pathname.match(/^\/collections\/[^/]+\/products\/([^/?#]+)\/?$/);
    if (!m) throw new Error('Invalid URL: ' + v);
    return { hostname: u.hostname.toLowerCase(), handle: m[1] };
  }

  // ─── Offer-input rows (dynamic, single mode) ────────────────────────────
  function addOfferRow(initialValue) {
    if (!offerListEl) return null;
    const row = document.createElement('div');
    row.className = 'sac-offer-row';
    row.innerHTML =
      '<input class="sac-form__input sac-offer-row__input" type="url" data-sac-offer-input placeholder="https://hostname.com/products/handle" spellcheck="false" autocapitalize="off">' +
      '<button type="button" class="sac-offer-row__remove" data-sac-offer-remove aria-label="Remove">×</button>';
    offerListEl.appendChild(row);

    const input = row.querySelector('[data-sac-offer-input]');
    if (typeof initialValue === 'string') input.value = initialValue;

    row.querySelector('[data-sac-offer-remove]').addEventListener('click', function () {
      const all = offerListEl.querySelectorAll('.sac-offer-row');
      if (all.length <= 1) { input.value = ''; return; }
      row.remove();
    });

    return input;
  }

  function readOfferInputs() {
    if (!offerListEl) return [];
    return Array.from(offerListEl.querySelectorAll('[data-sac-offer-input]'))
      .map(function (el) { return el.value.trim(); })
      .filter(Boolean);
  }

  function offerInputsAllValid() {
    if (!offerListEl) return false;
    const inputs = offerListEl.querySelectorAll('[data-sac-offer-input]');
    if (inputs.length === 0) return false;
    for (let i = 0; i < inputs.length; i++) {
      const v = inputs[i].value.trim();
      if (!v) return false;
      try { parseProductUrl(v); } catch (e) { return false; }
    }
    return true;
  }

  if (offerAddBtn) {
    offerAddBtn.addEventListener('click', function () {
      clearError();
      if (!offerInputsAllValid()) {
        showError('Fill every existing offer URL with a valid /products/<handle> link before adding another.');
        return;
      }
      const inp = addOfferRow('');
      if (inp) inp.focus();
    });
  }

  // ─── Build the 8 clickable preview cards ────────────────────────────────
  function renderOfferCards() {
    if (!cardsHost) return;
    cardsHost.innerHTML = OFFERS_CATALOG.map(function (row) {
      return '' +
        '<button type="button" class="sac-offer-card sac-offer-card--clickable" data-sac-card' +
          ' data-offer-id="' + escapeHtml(row.offerId) + '">' +
          '<span class="sac-offer-card__inner">' +
            '<span class="sac-offer-card__head">' +
              '<span class="sac-offer-card__badge" data-bundle-type="' + escapeHtml(row.offerType) + '">' + escapeHtml(row.offerType) + '</span>' +
              '<span class="sac-offer-card__id">' + escapeHtml(row.offerId) + '</span>' +
            '</span>' +
            '<span class="sac-offer-card__industry">' + escapeHtml(row.industry) + '</span>' +
            '<span class="sac-offer-card__sub">' + escapeHtml(row.subIndustry) + '</span>' +
            '<span class="sac-offer-card__cta">Preview →</span>' +
          '</span>' +
        '</button>';
    }).join('');

    cardsHost.querySelectorAll('[data-sac-card]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        const offerId = btn.getAttribute('data-offer-id');
        handleCardClick(offerId);
      });
    });
  }

  // ─── Filter dropdowns (Offer ID / Type / Industry / Sub-industry) ───────
  function populateFilterDropdowns() {
    const uniq = function (arr) { return Array.from(new Set(arr)); };

    const TYPE_LABELS = {
      FBT: 'Frequently Bought Together (FBT)',
      PB:  'Product Bundle (PB)'
    };

    function toTitle(s) {
      return String(s).toLowerCase().replace(/\b\w/g, function (c) { return c.toUpperCase(); });
    }

    function labelFor(field, value) {
      if (field === 'offerType') return TYPE_LABELS[value] || value;
      if (field === 'offerId') {
        const row = OFFERS_CATALOG.find(function (r) { return r.offerId === value; });
        if (row) return value + ' · ' + toTitle(row.industry) + ' (' + row.offerType + ')';
        return value;
      }
      return toTitle(value);
    }

    filterSelects.forEach(function (sel) {
      const field = sel.getAttribute('data-sac-filter');
      const values = uniq(OFFERS_CATALOG.map(function (r) { return r[field]; }));
      values.forEach(function (v) {
        const o = document.createElement('option');
        o.value = v;
        o.textContent = labelFor(field, v);
        sel.appendChild(o);
      });
    });
  }

  function readActiveFilters() {
    const f = {};
    filterSelects.forEach(function (sel) {
      const v = sel.value;
      if (v) f[sel.getAttribute('data-sac-filter')] = v;
    });
    return f;
  }

  function applyCardsFilter() {
    if (!cardsHost) return;
    const active = readActiveFilters();
    const keys = Object.keys(active);
    let visibleCount = 0;
    cardsHost.querySelectorAll('[data-sac-card]').forEach(function (btn) {
      const offerId = btn.getAttribute('data-offer-id');
      const row = OFFERS_CATALOG.find(function (r) { return r.offerId === offerId; });
      let show = true;
      if (row) {
        for (let i = 0; i < keys.length; i++) {
          if (row[keys[i]] !== active[keys[i]]) { show = false; break; }
        }
      }
      btn.hidden = !show;
      if (show) visibleCount++;
    });
    if (cardsEmptyEl) cardsEmptyEl.hidden = !(keys.length > 0 && visibleCount === 0);
  }

  filterSelects.forEach(function (sel) {
    sel.addEventListener('change', applyCardsFilter);
  });

  function setFiltersEnabled(enabled) {
    filterSelects.forEach(function (sel) {
      sel.disabled = !enabled;
      if (!enabled) sel.value = '';
    });
  }

  function setCardsEnabled(enabled) {
    if (!cardsWrap) return;
    cardsWrap.setAttribute('aria-disabled', enabled ? 'false' : 'true');
    if (cardsHint) cardsHint.hidden = enabled;
    setFiltersEnabled(enabled);
    if (!enabled && cardsEmptyEl) cardsEmptyEl.hidden = true;
  }

  // ─── Fetch helper: race .js against .json, prefer .js ───────────────────
  function convertPriceToCents(price) {
    if (!price && price !== 0) return null;
    if (typeof price === 'number' && Number.isInteger(price)) return price;
    return Math.round(parseFloat(price) * 100);
  }

  function enrichFromJson(jsonBody) {
    if (!jsonBody || typeof jsonBody !== 'object') {
      throw new Error('enrichFromJson: missing body');
    }
    const p = jsonBody.product;
    if (!p || typeof p !== 'object') {
      throw new Error('enrichFromJson: missing product envelope');
    }
    const variants = Array.isArray(p.variants) ? p.variants : [];
    const images   = Array.isArray(p.images)   ? p.images   : [];

    for (const v of variants) {
      v.available      = true;
      v.featured_image = images.find(function (img) { return img && img.id === v.image_id; }) || null;
      v.name           = v.title === 'Default Title' ? p.title : p.title + ' - ' + v.title;
      v.public_title   = v.title === 'Default Title' ? null : v.title;
      v.options        = [v.option1, v.option2, v.option3].filter(Boolean);
    }

    const prices         = variants.map(function (v) { return convertPriceToCents(v.price);              }).filter(function (n) { return n != null; });
    const comparePrices  = variants.map(function (v) { return convertPriceToCents(v.compare_at_price);   }).filter(function (n) { return n != null; });

    p.price                    = prices.length ? prices[0] : 0;
    p.price_min                = prices.length ? Math.min.apply(null, prices) : 0;
    p.price_max                = prices.length ? Math.max.apply(null, prices) : 0;
    p.price_varies             = p.price_min !== p.price_max;
    p.compare_at_price         = comparePrices.length ? comparePrices[0] : null;
    p.compare_at_price_min     = comparePrices.length ? Math.min.apply(null, comparePrices) : null;
    p.compare_at_price_max     = comparePrices.length ? Math.max.apply(null, comparePrices) : null;
    p.compare_at_price_varies  = p.compare_at_price_min !== p.compare_at_price_max;
    p.available                = variants.some(function (v) { return v.available; });
    p.featured_image           = (images[0] && images[0].src) || null;
    p.description              = p.body_html != null ? p.body_html : '';
    p.type                     = p.product_type != null ? p.product_type : '';
    p.url                      = '/products/' + p.handle;
    p.media                    = [];
    p.requires_selling_plan    = false;
    p.selling_plan_groups      = [];

    return p;
  }

  async function fetchShopifyProduct(domain, handle) {
    const jsUrl   = 'https://' + domain + '/products/' + handle + '.js';
    const jsonUrl = 'https://' + domain + '/products/' + handle + '.json';
    const init    = { method: 'GET', headers: { Accept: 'application/json' } };

    const jsPromise   = fetch(jsUrl,   init).catch(function (err) { return { __error: err }; });
    const jsonPromise = fetch(jsonUrl, init).catch(function (err) { return { __error: err }; });

    const discardJson = function () {
      jsonPromise.then(function (res) {
        if (!res || res.__error) return;
        if (res.body && typeof res.body.cancel === 'function') {
          res.body.cancel().catch(function () { /* swallow */ });
        }
      }).catch(function () { /* swallow */ });
    };

    const jsRes = await jsPromise;
    if (jsRes && !jsRes.__error && jsRes.ok) {
      try {
        const body = await jsRes.json();
        discardJson();
        return body;
      } catch (e) {
        console.warn('[store-analysis-custom] .js parse failed for', handle, e);
      }
    }

    const jsonRes = await jsonPromise;
    if (!jsonRes || jsonRes.__error || !jsonRes.ok) {
      return null;
    }
    let jsonBody;
    try {
      jsonBody = await jsonRes.json();
    } catch (e) {
      console.warn('[store-analysis-custom] .json parse failed for', handle, e);
      return null;
    }
    try {
      return enrichFromJson(jsonBody);
    } catch (e) {
      console.warn('[store-analysis-custom] enrichFromJson failed for', handle, e);
      return null;
    }
  }

  // ─── Validation ─────────────────────────────────────────────────────────

  function lookupFbtEntry(offerId) {
    const fbt = (window.gfb && window.gfb.settings && window.gfb.settings.frequentBoughtData) || [];
    const entry = fbt.find(function (o) { return o && o.offerId === offerId; });
    if (!entry || !entry.bundleShopifyData || !entry.bundleShopifyData.handle) {
      throw new Error('FBT data for ' + offerId + ' not loaded yet. Reload the page after FlyBundles initializes.');
    }
    return entry;
  }

  function shapeProduct(p) {
    const v = p.variants && p.variants[0];
    const img0 = p.images && p.images[0];
    const image = typeof img0 === 'string' ? img0 : (img0 && img0.src) || null;
    return {
      handle: p.handle,
      title:  p.title,
      image:  image,
      price:  v ? v.price : null,
      vendor: p.vendor || null
    };
  }

  function buildParentDetails(parentProduct) {
    const images = (parentProduct.images || [])
      .map(function (img) { return typeof img === 'string' ? img : (img && img.src); })
      .filter(Boolean);
    return {
      title: parentProduct.title || '',
      images: images
    };
  }

  // Build a context from one parent URL + N offer URLs.
  // Returns { name, sourceShop, parent:{handle,product}, offers:[{handle,product}], parentDetails }
  async function buildContext(name, parentRaw, offerRaws) {
    const parent = parseProductUrl(parentRaw);
    const offers = offerRaws.map(parseProductUrl);
    const sourceShop = parent.hostname;
    for (let i = 0; i < offers.length; i++) {
      if (offers[i].hostname !== sourceShop) {
        throw new Error('Hostname mismatch: "' + offerRaws[i] + '" must be on ' + sourceShop + '.');
      }
    }
    const parentP = fetchShopifyProduct(sourceShop, parent.handle);
    const offerPs = offers.map(function (o) { return fetchShopifyProduct(sourceShop, o.handle); });
    const results = await Promise.all([parentP].concat(offerPs));
    const parentProduct = results[0];
    if (!parentProduct) throw new Error('Could not load product: ' + parent.handle);
    const offerProducts = results.slice(1);
    for (let i = 0; i < offerProducts.length; i++) {
      if (!offerProducts[i]) throw new Error('Could not load product: ' + offers[i].handle);
    }
    return {
      name: name || (sourceShop + ' · ' + parent.handle),
      sourceShop: sourceShop,
      parent:  { handle: parent.handle, product: parentProduct },
      offers:  offers.map(function (o, i) { return { handle: o.handle, product: offerProducts[i] }; }),
      parentDetails: buildParentDetails(parentProduct)
    };
  }

  // ─── URL persistence (single mode only) ─────────────────────────────────
  function writeUrlState(parentRaw, offerRaws) {
    if (mode !== 'single') return;
    try {
      const u = new URL(window.location.href);
      u.searchParams.delete('parent');
      u.searchParams.delete('offer');
      if (parentRaw) u.searchParams.set('parent', parentRaw);
      (offerRaws || []).forEach(function (v) { if (v) u.searchParams.append('offer', v); });
      window.history.replaceState({}, '', u.toString());
    } catch (e) { /* ignore */ }
  }

  function readUrlState() {
    const params = new URLSearchParams(window.location.search);
    return {
      parent: params.get('parent') || '',
      offers: params.getAll('offer').filter(Boolean)
    };
  }

  // ─── Bulk-picker dropdown ───────────────────────────────────────────────
  function populateBulkPicker() {
    if (!bulkSelect) return;
    bulkSelect.innerHTML = '';
    bulkContexts.forEach(function (ctx, i) {
      const opt = document.createElement('option');
      opt.value = String(i);
      opt.textContent = (i + 1) + '. ' + ctx.name;
      bulkSelect.appendChild(opt);
    });
  }

  function setActiveBulkIndex(i) {
    if (!bulkContexts.length) { currentContext = null; return; }
    const idx = Math.max(0, Math.min(i, bulkContexts.length - 1));
    currentContext = bulkContexts[idx];
    if (bulkSelect) bulkSelect.value = String(idx);
    renderSummary();
  }

  if (bulkSelect) {
    bulkSelect.addEventListener('change', function () {
      setActiveBulkIndex(parseInt(bulkSelect.value, 10) || 0);
    });
  }

  // ─── Add flow ───────────────────────────────────────────────────────────

  async function runAddFlowSingle() {
    clearError();
    const parentRaw = parentUrlInp ? parentUrlInp.value.trim() : '';
    if (!parentRaw) { showError('Parent product URL is required.'); return; }
    const offerRaws = readOfferInputs();
    if (offerRaws.length === 0) { showError('Add at least one offer product URL.'); return; }

    if (submitBtn) submitBtn.disabled = true;
    try {
      const ctx = await buildContext(null, parentRaw, offerRaws);
      currentContext = ctx;
      bulkContexts = [];
      writeUrlState(parentRaw, offerRaws);
      if (bulkPickerWrap) bulkPickerWrap.setAttribute('hidden', '');
      renderSummary();
      setCardsEnabled(true);
    } catch (err) {
      console.error('[store-analysis-custom] single Add error', err);
      showError(err.message || 'Something went wrong.');
    } finally {
      if (submitBtn) submitBtn.disabled = false;
    }
  }

  async function runAddFlowBulk() {
    clearError();
    const raw = bulkTextarea ? bulkTextarea.value.trim() : '';
    if (!raw) { showError('Paste a JSON array of offers first.'); return; }

    let parsed;
    try { parsed = JSON.parse(raw); }
    catch (e) { showError('Invalid JSON: ' + e.message); return; }
    if (!Array.isArray(parsed) || parsed.length === 0) {
      showError('JSON must be a non-empty array of offers.');
      return;
    }

    if (submitBtn) submitBtn.disabled = true;
    try {
      const contexts = [];
      for (let i = 0; i < parsed.length; i++) {
        const entry = parsed[i];
        if (!entry || typeof entry !== 'object') {
          throw new Error('Entry #' + (i + 1) + ' is not an object.');
        }
        const parent = entry.parentProductUrl;
        const offers = entry.childProductUrls;
        if (!parent || typeof parent !== 'string') {
          throw new Error('Entry #' + (i + 1) + ' is missing a string "parentProductUrl".');
        }
        if (!Array.isArray(offers) || offers.length === 0) {
          throw new Error('Entry #' + (i + 1) + ' is missing a non-empty "childProductUrls" array.');
        }
        const label = typeof entry.label === 'string' && entry.label.trim()
          ? entry.label.trim()
          : 'Offer-' + (i + 1);
        contexts.push(await buildContext(label, parent, offers));
      }

      bulkContexts = contexts;
      populateBulkPicker();
      if (bulkPickerWrap) bulkPickerWrap.removeAttribute('hidden');
      setActiveBulkIndex(0);
      setCardsEnabled(true);
    } catch (err) {
      console.error('[store-analysis-custom] bulk Add error', err);
      showError(err.message || 'Bulk load failed.');
    } finally {
      if (submitBtn) submitBtn.disabled = false;
    }
  }

  async function handleAdd(e) {
    e.preventDefault();
    if (mode === 'bulk') await runAddFlowBulk();
    else await runAddFlowSingle();
  }

  function renderSummary() {
    if (!currentContext || !summaryEl) return;
    if (summaryHostEl)   summaryHostEl.textContent   = currentContext.sourceShop;
    if (summaryParentEl) summaryParentEl.textContent = currentContext.parent.handle;
    if (summaryOffersEl) {
      const handles = currentContext.offers.map(function (o) { return o.handle; });
      summaryOffersEl.textContent = handles.join(', ');
    }
    summaryEl.removeAttribute('hidden');
  }

  // ─── Card click: open preview tab using the cached context ──────────────

  function handleCardClick(offerId) {
    clearError();
    if (!currentContext) {
      showError('Click Add first to set parent and offer products.');
      return;
    }
    const row = OFFERS_CATALOG.find(function (r) { return r.offerId === offerId; });
    if (!row) { showError('Unknown offer.'); return; }

    let fbtEntry;
    try { fbtEntry = lookupFbtEntry(offerId); }
    catch (err) { showError(err.message); return; }

    const resolveParentProductHandle = function () {
      if (fbtEntry.type && String(fbtEntry.type).includes("CUSTOM_BUNDLE")) {
        return fbtEntry.bundleShopifyData.handle;
      }
      const widgetDisplayOptions = fbtEntry.widgetDisplayOptions || {};
      const specificProductPages = widgetDisplayOptions.specificProductPages;
      const specificProducts = widgetDisplayOptions.specificProducts || [];
      if (specificProductPages && specificProducts.length > 0) {
        return specificProducts[0].handle;
      }
      return fbtEntry.bundleShopifyData.handle;
    };

    const payload = {
      sourceShop: currentContext.sourceShop,
      offerId: row.offerId,
      productHandle: resolveParentProductHandle(),
      offerProducts: currentContext.offers.map(function (o) { return shapeProduct(o.product); }),
      offerType: row.offerType,
      industry: row.industry,
      subIndustry: row.subIndustry,
      parentProductDetails: currentContext.parentDetails,
      createdAt: Date.now()
    };

    const targetUrl = '/products/' + payload.productHandle + '?preview_mode=true&variant_design=auto';
    const newTab = window.open(targetUrl, '_blank');
    if (!newTab) {
      showError('The browser blocked the preview tab. Allow pop-ups for this site and try again.');
      return;
    }
    newTab.storeAnalysisSkailamaPreview = payload;
    newTab.addEventListener('DOMContentLoaded', function () {
      newTab.storeAnalysisSkailamaPreview = payload;
      try { newTab.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload)); }
      catch (e) { /* sessionStorage may fail in private mode */ }
    });
  }

  // ─── Boot ───────────────────────────────────────────────────────────────
  renderOfferCards();
  populateFilterDropdowns();
  setCardsEnabled(false);

  const initial = readUrlState();
  if (initial.parent && parentUrlInp) parentUrlInp.value = initial.parent;
  if (initial.offers.length > 0) {
    initial.offers.forEach(function (v) { addOfferRow(v); });
  } else {
    addOfferRow('');
  }

  if (form) form.addEventListener('submit', handleAdd);

  // Default to Bulk JSON unless the URL carries single-mode state.
  if (initial.parent && initial.offers.length > 0) {
    setMode('single');
    runAddFlowSingle();
  } else {
    setMode('bulk');
  }
})();
